import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { useTheme } from '../context/ThemeContext.jsx';
import { useNotification } from '../context/NotificationContext.jsx';
import axios from 'axios';
import {
  Settings as SettingsIcon,
  User,
  Shield,
  Bell,
  Lock,
  CreditCard,
  Eye,
  EyeOff,
  Camera,
  Download,
  AlertTriangle,
  CheckCircle,
  XCircle,
  AlertCircle,
  Crown,
  Zap,
  Calendar,
  Clock,
  DollarSign,
  FileText,
  Sun,
  Moon,
  Monitor
} from 'lucide-react';
import {
  createCheckout,
  getSubscription,
  cancelSubscription
} from '../services/api';
import './Settings.css';

const Settings = () => {
  const { user, updateProfile } = useAuth();
  const { theme, setThemeMode, isDark } = useTheme();
  const {
    enableNotifications,
    notificationPermission,
    isSupported: isNotificationSupported,
    checkPermission
  } = useNotification();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Profile form state
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    website: '',
    timezone: 'UTC',
    bio: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Password visibility toggles
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Billing state
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState('stripe');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Currency state
  const [selectedCurrency, setSelectedCurrency] = useState('USD');

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    pushEnabled: false,
    emailNotifications: {
      courseUpdates: true,
      newLessons: true,
      webinarReminders: true,
      assignmentDeadlines: true,
      certificateEarned: true,
      paymentBilling: true,
      marketing: false
    },
    emailFrequency: 'instant', // 'instant' | 'daily' | 'weekly'
    categories: {
      courseContent: true,
      systemAnnouncements: true,
      liveStreaming: true,
      promotions: true,
      accountBilling: true
    }
  });

  // Load user data on mount
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        location: user.location || '',
        website: user.website || '',
        timezone: user.timezone || 'UTC',
        bio: user.bio || ''
      });
    }
  }, [user]);

  // Auto-hide message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Show message helper
  const showMessage = (type, text) => {
    setMessage({ type, text });
  };

  // Handle profile input changes
  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  // Handle password input changes
  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateProfile(profileData);
      showMessage('success', 'Profile updated successfully!');
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.put('/auth/updatepassword', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      showMessage('success', 'Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Handle data export
  const handleExportData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/user/export', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user-data-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showMessage('success', 'Data exported successfully!');
    } catch (error) {
      showMessage('error', 'Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone.'
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      await axios.delete('/user/account');
      showMessage('success', 'Account deleted successfully. Redirecting...');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      showMessage('error', 'Failed to delete account');
      setLoading(false);
    }
  };

  // Get user initials
  const getInitials = () => {
    const firstName = profileData.firstName || user?.firstName || '';
    const lastName = profileData.lastName || user?.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  // Fetch subscription data
  const fetchSubscription = async () => {
    setLoadingSubscription(true);
    try {
      const response = await getSubscription();
      setSubscription(response.data.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      showMessage('error', 'Failed to load subscription details');
    } finally {
      setLoadingSubscription(false);
    }
  };

  // Load subscription when billing tab is active
  useEffect(() => {
    if (activeTab === 'billing' && !subscription) {
      fetchSubscription();
    }
  }, [activeTab]);

  // Debug: Log state changes for payment flow
  useEffect(() => {
    console.log('🔍 Payment State Update:', {
      selectedTier,
      selectedGateway,
      showUpgradeModal,
      processingPayment
    });
  }, [selectedTier, selectedGateway, showUpgradeModal, processingPayment]);

  // Handle upgrade initiation
  const handleUpgradeClick = (tier) => {
    console.log('🚀 Upgrade button clicked for tier:', tier);
    setSelectedTier(tier);
    setShowUpgradeModal(true);
    console.log('✅ Modal opened, selectedTier set to:', tier);
  };

  // Handle payment processing
  const handleProcessPayment = async () => {
    console.log('🔄 handleProcessPayment called');
    console.log('📊 Selected Tier:', selectedTier);
    console.log('💳 Selected Gateway:', selectedGateway);

    if (!selectedTier || !selectedGateway) {
      console.error('❌ Validation failed: Missing tier or gateway');
      showMessage('error', 'Please select a tier and payment method');
      return;
    }

    setProcessingPayment(true);
    console.log('⏳ Processing payment...');

    try {
      console.log('📤 Calling createCheckout API with:', {
        tier: selectedTier,
        paymentGateway: selectedGateway
      });

      const response = await createCheckout({
        tier: selectedTier,
        paymentGateway: selectedGateway
      });

      console.log('📥 API Response:', response);

      if (response.data.success) {
        const { payment } = response.data.data;
        console.log('✅ Checkout session created:', payment);

        // Redirect to payment gateway - unified approach for all gateways
        let redirectUrl = null;

        if (payment.gateway === 'stripe') {
          // Check for Stripe Checkout URL (preferred) or construct from session
          if (payment.url) {
            redirectUrl = payment.url;
            console.log('💳 Stripe Checkout URL found:', redirectUrl);
          } else if (payment.sessionUrl) {
            redirectUrl = payment.sessionUrl;
            console.log('💳 Stripe Session URL found:', redirectUrl);
          } else if (payment.clientSecret) {
            // Fallback: if only clientSecret is provided, inform user
            console.warn('⚠️ Stripe returned clientSecret but no checkout URL');
            showMessage('error', 'Stripe checkout URL not available. Please contact support.');
            setProcessingPayment(false);
            return;
          }
        } else if (payment.gateway === 'paypal') {
          redirectUrl = payment.approveUrl;
          console.log('💰 PayPal approve URL:', redirectUrl);
        } else if (payment.gateway === 'billplz') {
          redirectUrl = payment.url;
          console.log('🏦 Billplz payment URL:', redirectUrl);
        }

        // Redirect to payment page
        if (redirectUrl) {
          console.log('🔗 Redirecting to payment gateway:', redirectUrl);
          showMessage('success', 'Redirecting to payment gateway...');

          // Close modal before redirect
          setShowUpgradeModal(false);

          // Redirect after short delay to show message
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 500);
        } else {
          console.error('❌ No redirect URL found in payment response');
          showMessage('error', 'Payment redirect URL not available');
        }
      } else {
        console.error('❌ API returned success:false');
        showMessage('error', 'Payment session creation failed');
      }
    } catch (error) {
      console.error('❌ Payment error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);

      const errorMessage = error.response?.data?.message || error.message || 'Failed to create checkout session';
      showMessage('error', errorMessage);
    } finally {
      setProcessingPayment(false);
      console.log('✅ Payment processing complete');
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel your subscription? You will lose access to premium features.'
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await cancelSubscription();
      if (response.data.success) {
        showMessage('success', 'Subscription cancelled successfully');
        fetchSubscription(); // Refresh subscription data
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  // Currency options and conversion rates
  const currencies = {
    USD: { symbol: '$', label: 'USD (US Dollar)', rate: 1 },
    MYR: { symbol: 'RM', label: 'MYR (Malaysian Ringgit)', rate: 4.60 },
    IDR: { symbol: 'Rp', label: 'IDR (Indonesian Rupiah)', rate: 15800 },
    SGD: { symbol: 'S$', label: 'SGD (Singapore Dollar)', rate: 1.35 },
    THB: { symbol: '฿', label: 'THB (Thai Baht)', rate: 35 }
  };

  // Load currency preference from localStorage on mount
  useEffect(() => {
    const savedCurrency = localStorage.getItem('preferredCurrency');
    if (savedCurrency && currencies[savedCurrency]) {
      setSelectedCurrency(savedCurrency);
    } else {
      // Optional: Auto-detect from browser locale
      try {
        const userLocale = navigator.language || navigator.userLanguage;
        if (userLocale.includes('MY')) setSelectedCurrency('MYR');
        else if (userLocale.includes('ID')) setSelectedCurrency('IDR');
        else if (userLocale.includes('SG')) setSelectedCurrency('SGD');
        else if (userLocale.includes('TH')) setSelectedCurrency('THB');
      } catch (e) {
        // Fallback to USD if detection fails
        setSelectedCurrency('USD');
      }
    }
  }, []);

  // Save currency preference to localStorage when changed
  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    localStorage.setItem('preferredCurrency', currency);
  };

  // Convert USD price to selected currency
  const convertPrice = (usdPrice) => {
    if (usdPrice === 0) return 0;
    const rate = currencies[selectedCurrency]?.rate || 1;
    return usdPrice * rate;
  };

  // Format price with currency symbol
  const formatPrice = (usdPrice) => {
    if (usdPrice === 0) return 'Free';

    const convertedPrice = convertPrice(usdPrice);
    const currencyInfo = currencies[selectedCurrency];

    // Format based on currency
    if (selectedCurrency === 'IDR') {
      // Indonesian Rupiah - no decimals, with thousand separators
      return `${currencyInfo.symbol}${Math.round(convertedPrice).toLocaleString('id-ID')}`;
    } else if (selectedCurrency === 'MYR' || selectedCurrency === 'SGD' || selectedCurrency === 'THB') {
      // Malaysian Ringgit, Singapore Dollar, Thai Baht - 2 decimals
      return `${currencyInfo.symbol}${convertedPrice.toFixed(2)}`;
    } else {
      // USD - 2 decimals
      return `${currencyInfo.symbol}${convertedPrice.toFixed(2)}`;
    }
  };

  // Notification Settings Handlers
  const handleEnablePushNotifications = async () => {
    setLoading(true);
    try {
      const result = await enableNotifications();
      if (result.success) {
        setNotificationPrefs(prev => ({ ...prev, pushEnabled: true }));
        showMessage('success', 'Push notifications enabled successfully!');
        await saveNotificationPreferences({ ...notificationPrefs, pushEnabled: true });
      } else {
        showMessage('error', result.error || 'Failed to enable notifications');
      }
    } catch (error) {
      showMessage('error', 'Failed to enable push notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailNotificationToggle = async (key) => {
    const updated = {
      ...notificationPrefs,
      emailNotifications: {
        ...notificationPrefs.emailNotifications,
        [key]: !notificationPrefs.emailNotifications[key]
      }
    };
    setNotificationPrefs(updated);
    await saveNotificationPreferences(updated);
  };

  const handleEmailFrequencyChange = async (frequency) => {
    const updated = { ...notificationPrefs, emailFrequency: frequency };
    setNotificationPrefs(updated);
    await saveNotificationPreferences(updated);
  };

  const handleCategoryToggle = async (category) => {
    const updated = {
      ...notificationPrefs,
      categories: {
        ...notificationPrefs.categories,
        [category]: !notificationPrefs.categories[category]
      }
    };
    setNotificationPrefs(updated);
    await saveNotificationPreferences(updated);
  };

  const saveNotificationPreferences = async (prefs) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/preferences`,
        prefs,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      showMessage('success', 'Notification preferences saved');
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      showMessage('error', 'Failed to save preferences');
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/notifications/preferences`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      if (response.data.success && response.data.data) {
        setNotificationPrefs(response.data.data);
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
  };

  // Load notification preferences when notifications tab is active
  useEffect(() => {
    if (activeTab === 'notifications' && user) {
      loadNotificationPreferences();
      checkPermission();
    }
  }, [activeTab, user]);

  // Get tier details
  const getTierDetails = (tier) => {
    const tiers = {
      free: {
        name: 'Free',
        price: 0,
        currency: 'USD',
        features: ['5 AI requests per month', 'Basic course access', 'Community support'],
        icon: User,
        color: 'gray'
      },
      vip: {
        name: 'VIP',
        price: 5.00,
        currency: 'USD',
        features: [
          '1K Generations/month - Generate up to 1,000 indicators per month',
          'Advanced AI Powered - Access to advanced AI for better code quality',
          'Generation History - Access all your previously generated code',
          '10+ Code Templates - Ready-to-use indicators',
          'Advanced Alert System Version 2 - Next-generation notification system',
          'Code Comments & Documentation - AI-explained code with detailed comments'
        ],
        icon: Zap,
        color: 'blue'
      },
      premium: {
        name: 'Premium',
        price: 15.00,
        currency: 'USD',
        features: [
          'Unlimited Generations - Generate as many indicators as you need - no limits!',
          'Advanced AI Powered - Access to advanced AI for better code quality',
          'Built-in SL & TP - Stop Loss & Take Profit automatically integrated',
          'Generation History - Access all your previously generated code',
          '50+ Code Templates - Premium collection of ready-to-use indicators',
          'Advanced Alert System Version 3 - Latest alert technology with advanced features',
          'Template Alert to Telegram - Send trading alerts directly to your Telegram',
          'Code Comments & Documentation - AI-explained code with detailed comments'
        ],
        icon: Crown,
        color: 'gold'
      }
    };
    return tiers[tier] || tiers.free;
  };

  // Tabs configuration
  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'account', label: 'Account', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'preferences', label: 'Preferences', icon: SettingsIcon },
    { id: 'privacy', label: 'Privacy', icon: Lock },
    { id: 'billing', label: 'Billing', icon: CreditCard }
  ];

  return (
    <div className="settings-page">
      {/* Status Message */}
      {message && (
        <div className={`status-message ${message.type}`}>
          <div className="status-content">
            {message.type === 'success' && <CheckCircle size={20} />}
            {message.type === 'error' && <XCircle size={20} />}
            {message.type === 'warning' && <AlertCircle size={20} />}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="settings-header">
        <div className="header-content">
          <div className="header-title-group">
            <div className="header-icon">
              <SettingsIcon size={28} />
            </div>
            <div>
              <h1>Settings</h1>
              <p className="subtitle">Manage your account settings and preferences</p>
            </div>
          </div>
          <div className="breadcrumb">
            <span>Dashboard</span>
            <span className="separator">/</span>
            <span className="current">Settings</span>
          </div>
        </div>
      </div>

      {/* Settings Container */}
      <div className="settings-container">
        {/* Tabs Navigation */}
        <div className="settings-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
                {tab.comingSoon && <span className="coming-soon-badge">Soon</span>}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="settings-content">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="tab-panel">
              <h2 className="panel-title">Profile Settings</h2>
              <p className="panel-description">Update your profile information and photo</p>

              {/* Profile Photo Section */}
              <div className="profile-photo-section">
                <div className="profile-photo">
                  <div className="photo-placeholder">
                    {getInitials()}
                  </div>
                </div>
                <div className="photo-info">
                  <button className="btn-photo">
                    <Camera size={16} />
                    Change Photo
                  </button>
                  <p className="photo-hint">JPG, PNG or GIF. Max size 2MB</p>
                </div>
              </div>

              {/* Profile Form */}
              <form onSubmit={handleProfileUpdate} className="settings-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleProfileChange}
                      placeholder="Enter first name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleProfileChange}
                      placeholder="Enter last name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      placeholder="Enter email"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleProfileChange}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="location">Location</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={profileData.location}
                      onChange={handleProfileChange}
                      placeholder="City, Country"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="website">Website</label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={profileData.website}
                      onChange={handleProfileChange}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="timezone">Timezone</label>
                    <select
                      id="timezone"
                      name="timezone"
                      value={profileData.timezone}
                      onChange={handleProfileChange}
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                      <option value="Asia/Tokyo">Tokyo</option>
                    </select>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="bio">Bio</label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={profileData.bio}
                    onChange={handleProfileChange}
                    placeholder="Write a short bio about yourself..."
                    rows="4"
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="tab-panel">
              <h2 className="panel-title">Account Settings</h2>
              <p className="panel-description">Manage your account security and preferences</p>

              {/* Change Password Section */}
              <div className="account-section">
                <h3 className="section-title">Change Password</h3>
                <form onSubmit={handlePasswordUpdate} className="settings-form">
                  <div className="form-group">
                    <label htmlFor="currentPassword">Current Password</label>
                    <div className="password-input">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => togglePasswordVisibility('current')}
                      >
                        {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <div className="password-input">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => togglePasswordVisibility('new')}
                      >
                        {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <div className="password-input">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => togglePasswordVisibility('confirm')}
                      >
                        {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Account Actions */}
              <div className="account-section">
                <h3 className="section-title">Account Actions</h3>

                <div className="account-action">
                  <div className="action-info">
                    <h4>Download Your Data</h4>
                    <p>Export all your account data and course progress</p>
                  </div>
                  <button
                    className="btn-outline"
                    onClick={handleExportData}
                    disabled={loading}
                  >
                    <Download size={16} />
                    Export
                  </button>
                </div>

                <div className="account-action danger">
                  <div className="action-info">
                    <h4>Delete Account</h4>
                    <p>Permanently delete your account and all associated data</p>
                  </div>
                  <button
                    className="btn-danger"
                    onClick={handleDeleteAccount}
                    disabled={loading}
                  >
                    <AlertTriangle size={16} />
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="tab-panel">
              <h2 className="panel-title">Preferences</h2>
              <p className="panel-description">Customize your application experience</p>

              {/* Theme Settings */}
              <div className="preferences-section">
                <h3 className="section-title">Appearance</h3>
                <p className="section-description">Choose how the application looks to you</p>

                <div className="theme-selector">
                  <div
                    className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                    onClick={() => setThemeMode('light')}
                  >
                    <div className="theme-option-icon">
                      <Sun size={32} />
                    </div>
                    <div className="theme-option-content">
                      <h4>Light Mode</h4>
                      <p>Clean and bright interface</p>
                    </div>
                    {theme === 'light' && (
                      <div className="theme-check">
                        <CheckCircle size={24} />
                      </div>
                    )}
                  </div>

                  <div
                    className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                    onClick={() => setThemeMode('dark')}
                  >
                    <div className="theme-option-icon dark">
                      <Moon size={32} />
                    </div>
                    <div className="theme-option-content">
                      <h4>Dark Mode</h4>
                      <p>Easy on the eyes in low light</p>
                    </div>
                    {theme === 'dark' && (
                      <div className="theme-check">
                        <CheckCircle size={24} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Current Theme Info */}
                <div className="current-theme-info">
                  <div className="theme-info-card">
                    <div className="theme-info-icon">
                      {isDark ? <Moon size={20} /> : <Sun size={20} />}
                    </div>
                    <div>
                      <h4>Current Theme</h4>
                      <p>{isDark ? 'Dark Mode' : 'Light Mode'} is active</p>
                    </div>
                  </div>
                  <div className="theme-info-hint">
                    <AlertCircle size={16} />
                    <span>Your theme preference is saved automatically</span>
                  </div>
                </div>
              </div>

              {/* Future Preferences Placeholder */}
              <div className="preferences-section">
                <h3 className="section-title">Other Preferences</h3>
                <div className="coming-soon-notice">
                  <Monitor size={48} />
                  <p>More preferences options coming soon</p>
                  <span>Language, timezone, notifications, and more</span>
                </div>
              </div>
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div className="tab-panel">
              <h2 className="panel-title">Billing & Subscription</h2>
              <p className="panel-description">Manage your subscription and payment methods</p>

              {/* Loading State */}
              {loadingSubscription && (
                <div className="loading-state">
                  <p>Loading subscription details...</p>
                </div>
              )}

              {/* Subscription Overview */}
              {!loadingSubscription && subscription && (
                <>
                  {/* Current Plan */}
                  <div className="billing-section">
                    <h3 className="section-title">Current Plan</h3>
                    <div className="current-plan-card">
                      <div className="plan-header">
                        <div className="plan-info">
                          {(() => {
                            const tierInfo = getTierDetails(subscription.tier);
                            const TierIcon = tierInfo.icon;
                            return (
                              <>
                                <div className={`plan-icon ${tierInfo.color}`}>
                                  <TierIcon size={24} />
                                </div>
                                <div>
                                  <h4>{tierInfo.name} Plan</h4>
                                  <p className="plan-price">
                                    {tierInfo.price === 0 ? 'Free' : `${formatPrice(tierInfo.price)} / month`}
                                  </p>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                        <div className={`plan-status ${subscription.status}`}>
                          {subscription.status === 'active' ? 'Active' : subscription.status}
                        </div>
                      </div>

                      {/* Plan Details */}
                      <div className="plan-details">
                        <div className="detail-item">
                          <Calendar size={16} />
                          <span>
                            Started: {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <Zap size={16} />
                          <span>
                            AI Usage: {subscription.currentMonthUsage || 0} / {subscription.monthlyLimit === -1 ? 'Unlimited' : subscription.monthlyLimit}
                          </span>
                        </div>
                        {subscription.remaining !== undefined && (
                          <div className="detail-item">
                            <Clock size={16} />
                            <span>
                              Remaining: {subscription.remaining === -1 ? 'Unlimited' : subscription.remaining}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="plan-actions">
                        {subscription.tier !== 'premium' && (
                          <button
                            className="btn-primary"
                            onClick={() => handleUpgradeClick(subscription.tier === 'free' ? 'vip' : 'premium')}
                          >
                            <Crown size={16} />
                            Upgrade Plan
                          </button>
                        )}
                        {subscription.tier !== 'free' && subscription.status === 'active' && (
                          <button
                            className="btn-outline btn-danger-outline"
                            onClick={handleCancelSubscription}
                            disabled={loading}
                          >
                            Cancel Subscription
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Available Plans */}
                  <div className="billing-section">
                    <div className="section-header-with-selector">
                      <h3 className="section-title">Available Plans</h3>

                      {/* Currency Selector */}
                      <div className="currency-selector">
                        <label htmlFor="currency-select">Display prices in:</label>
                        <select
                          id="currency-select"
                          value={selectedCurrency}
                          onChange={(e) => handleCurrencyChange(e.target.value)}
                          className="currency-dropdown"
                        >
                          {Object.keys(currencies).map((currencyCode) => (
                            <option key={currencyCode} value={currencyCode}>
                              {currencies[currencyCode].symbol} {currencyCode}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Currency Disclaimer */}
                    {selectedCurrency !== 'USD' && (
                      <div className="currency-disclaimer">
                        <AlertCircle size={16} />
                        <span>
                          Prices shown in {selectedCurrency} are approximate. Payment will be processed in USD.
                        </span>
                      </div>
                    )}

                    <div className="plans-grid">
                      {['free', 'vip', 'premium'].map((tier) => {
                        const tierInfo = getTierDetails(tier);
                        const TierIcon = tierInfo.icon;
                        const isCurrentPlan = subscription.tier === tier;

                        return (
                          <div
                            key={tier}
                            className={`plan-card ${isCurrentPlan ? 'current' : ''} ${tierInfo.color}`}
                          >
                            <div className="plan-card-header">
                              <TierIcon size={32} />
                              <h4>{tierInfo.name}</h4>
                              <div className="plan-card-price">
                                {tierInfo.price === 0 ? (
                                  <span className="price-free">Free</span>
                                ) : (
                                  <>
                                    <span className="price-amount">{formatPrice(tierInfo.price)}</span>
                                    <span className="price-currency">/ month</span>
                                  </>
                                )}
                              </div>
                            </div>

                            <ul className="plan-features">
                              {tierInfo.features.map((feature, idx) => (
                                <li key={idx}>
                                  <CheckCircle size={16} />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>

                            {isCurrentPlan ? (
                              <button className="btn-plan current" disabled>
                                Current Plan
                              </button>
                            ) : tier === 'free' ? (
                              <button
                                className="btn-plan"
                                onClick={handleCancelSubscription}
                                disabled={loading}
                              >
                                Downgrade to Free
                              </button>
                            ) : (
                              <button
                                className="btn-plan"
                                onClick={() => handleUpgradeClick(tier)}
                              >
                                Upgrade to {tierInfo.name}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Payment History */}
                  <div className="billing-section">
                    <h3 className="section-title">Payment History</h3>
                    <div className="payment-history">
                      <div className="empty-state">
                        <FileText size={48} />
                        <p>No payment history available</p>
                        <span className="empty-hint">Your payment transactions will appear here</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Upgrade Modal */}
          {showUpgradeModal && (
            <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Upgrade to {selectedTier && getTierDetails(selectedTier).name}</h3>
                  <button
                    className="modal-close"
                    onClick={() => setShowUpgradeModal(false)}
                  >
                    <XCircle size={24} />
                  </button>
                </div>

                <div className="modal-body">
                  {selectedTier && (
                    <>
                      <div className="upgrade-summary">
                        <p className="upgrade-price">
                          {formatPrice(getTierDetails(selectedTier).price)} / month
                        </p>
                        <ul className="upgrade-features">
                          {getTierDetails(selectedTier).features.map((feature, idx) => (
                            <li key={idx}>
                              <CheckCircle size={16} />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="payment-gateway-selector">
                        <label>Select Payment Method:</label>
                        <div className="gateway-options">
                          <button
                            className={`gateway-option ${selectedGateway === 'stripe' ? 'selected' : ''}`}
                            onClick={() => {
                              console.log('💳 Stripe gateway selected');
                              setSelectedGateway('stripe');
                            }}
                          >
                            <CreditCard size={20} />
                            <span>Credit Card (Stripe)</span>
                          </button>
                          <button
                            className={`gateway-option ${selectedGateway === 'paypal' ? 'selected' : ''}`}
                            onClick={() => {
                              console.log('💰 PayPal gateway selected');
                              setSelectedGateway('paypal');
                            }}
                          >
                            <DollarSign size={20} />
                            <span>PayPal</span>
                          </button>
                          <button
                            className={`gateway-option ${selectedGateway === 'billplz' ? 'selected' : ''}`}
                            onClick={() => {
                              console.log('🏦 Billplz gateway selected');
                              setSelectedGateway('billplz');
                            }}
                          >
                            <FileText size={20} />
                            <span>Billplz</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    className="btn-outline"
                    onClick={() => setShowUpgradeModal(false)}
                    disabled={processingPayment}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      console.log('🔘 Continue to Payment button clicked');
                      handleProcessPayment();
                    }}
                    disabled={processingPayment}
                  >
                    {processingPayment ? 'Processing...' : 'Continue to Payment'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <div className="tab-panel privacy-policy">
              <h2 className="panel-title">Dasar Privasi – Learn 2 Trade</h2>
              <p className="last-updated">Kali terakhir dikemas kini: 3 November 2025</p>

              <div className="privacy-content">
                <p className="privacy-intro">
                  Learn 2 Trade menghormati privasi setiap pengguna platform kami. Dasar Privasi ini menerangkan bagaimana kami mengumpul, menggunakan, dan melindungi maklumat peribadi anda apabila anda menggunakan platform pembelajaran kami.
                </p>

                <section className="privacy-section">
                  <h3>1. Tujuan Platform</h3>
                  <p>
                    Learn 2 Trade ialah Platform Pembelajaran Digital (Learning Management System) yang menyediakan modul latihan, bimbingan, serta alat bantuan seperti AI Coder bagi tujuan pendidikan dan peningkatan kemahiran dalam bidang perdagangan dan teknologi.
                  </p>
                  <p>
                    Kami tidak menawarkan perkhidmatan broker, pelaburan, atau pengurusan akaun perdagangan. Semua kandungan adalah untuk tujuan pembelajaran sahaja, selaras dengan garis panduan Suruhanjaya Sekuriti Malaysia (SC Malaysia).
                  </p>
                </section>

                <section className="privacy-section">
                  <h3>2. Maklumat Yang Dikumpul</h3>
                  <p>Kami mungkin mengumpul maklumat berikut:</p>
                  <ul>
                    <li>Nama dan alamat e-mel apabila anda mendaftar akaun;</li>
                    <li>Data penggunaan platform (contohnya kemajuan kursus, masa log masuk);</li>
                    <li>Maklumat teknikal (contohnya jenis peranti, pelayar web, alamat IP).</li>
                  </ul>
                  <p>
                    Maklumat ini digunakan bagi memastikan pengalaman pembelajaran anda lebih lancar dan diperibadikan.
                  </p>
                </section>

                <section className="privacy-section">
                  <h3>3. Cara Kami Menggunakan Maklumat</h3>
                  <p>Maklumat peribadi anda digunakan untuk:</p>
                  <ul>
                    <li>Memberi akses kepada kursus dan bahan pembelajaran;</li>
                    <li>Menambah baik fungsi platform dan sistem AI;</li>
                    <li>Mengurus akaun pengguna dan komunikasi sokongan;</li>
                    <li>Memastikan keselamatan sistem dan pencegahan penyalahgunaan.</li>
                  </ul>
                  <p>
                    Kami tidak menjual atau berkongsi data peribadi anda kepada mana-mana pihak ketiga untuk tujuan pemasaran.
                  </p>
                </section>

                <section className="privacy-section">
                  <h3>4. Perlindungan Data</h3>
                  <p>
                    Kami menggunakan langkah keselamatan teknikal dan organisasi untuk melindungi data anda daripada capaian tidak sah, kehilangan, atau penyalahgunaan. Semua komunikasi dalam platform dilindungi menggunakan protokol penyulitan standard industri.
                  </p>
                </section>

                <section className="privacy-section">
                  <h3>5. Hak Pengguna</h3>
                  <p>Anda berhak untuk:</p>
                  <ul>
                    <li>Mengakses dan mengemaskini maklumat akaun anda;</li>
                    <li>Meminta pemadaman data peribadi anda;</li>
                    <li>Menarik balik persetujuan terhadap pemprosesan data pada bila-bila masa.</li>
                  </ul>
                  <p>
                    Permintaan boleh dibuat melalui e-mel rasmi kami di <a href="mailto:support@learn2trade.my">support@learn2trade.my</a>.
                  </p>
                </section>

                <section className="privacy-section">
                  <h3>6. Pematuhan Undang-Undang</h3>
                  <p>
                    Learn 2 Trade mematuhi Akta Perlindungan Data Peribadi 2010 (PDPA) dan garis panduan Suruhanjaya Sekuriti Malaysia (SC). Kami menolak sebarang bentuk promosi pelaburan atau broker tidak berlesen di dalam platform kami.
                  </p>
                </section>

                <section className="privacy-section">
                  <h3>7. Perubahan Dasar Privasi</h3>
                  <p>
                    Kami berhak meminda Dasar Privasi ini dari semasa ke semasa. Sebarang perubahan akan diumumkan melalui laman web atau e-mel pengguna berdaftar.
                  </p>
                </section>

                <section className="privacy-section">
                  <h3>8. Hubungi Kami</h3>
                  <p>Untuk sebarang pertanyaan berkaitan privasi, sila hubungi:</p>
                  <div className="contact-info">
                    <p>📧 <a href="mailto:support@learn2trade.my">support@learn2trade.my</a></p>
                    <p>🌐 <a href="https://www.learn2trade.my" target="_blank" rel="noopener noreferrer">www.learn2trade.my</a></p>
                  </div>
                </section>
              </div>
            </div>
          )}

          {/* Notifications Settings */}
          {activeTab === 'notifications' && (
            <div className="tab-panel notifications-settings">
              <h2 className="panel-title">Notification Settings</h2>
              <p className="panel-description">
                Manage how you want to receive notifications and updates from Learn 2 Trade
              </p>

              {/* A) Push Notifications Section */}
              <div className="notification-section">
                <div className="section-header-row">
                  <div>
                    <h3 className="section-title">
                      <Bell size={20} />
                      Push Notifications
                    </h3>
                    <p className="section-description">
                      Receive real-time notifications even when you're not on the site
                    </p>
                  </div>

                  {/* Status Badge */}
                  <div className="notification-status">
                    {!isNotificationSupported ? (
                      <span className="status-badge unsupported">
                        <XCircle size={16} />
                        Not Supported
                      </span>
                    ) : notificationPermission === 'granted' ? (
                      <span className="status-badge enabled">
                        <CheckCircle size={16} />
                        Enabled
                      </span>
                    ) : notificationPermission === 'denied' ? (
                      <span className="status-badge blocked">
                        <AlertTriangle size={16} />
                        Blocked
                      </span>
                    ) : (
                      <span className="status-badge disabled">
                        <XCircle size={16} />
                        Disabled
                      </span>
                    )}
                  </div>
                </div>

                {/* Push Notification Controls */}
                <div className="notification-controls">
                  {!isNotificationSupported ? (
                    <div className="notification-warning">
                      <AlertTriangle size={20} />
                      <div>
                        <strong>Push notifications are not supported</strong>
                        <p>Your browser doesn't support push notifications. Please try using Chrome, Firefox, or Edge.</p>
                      </div>
                    </div>
                  ) : notificationPermission === 'denied' ? (
                    <div className="notification-warning">
                      <AlertTriangle size={20} />
                      <div>
                        <strong>Notifications are blocked</strong>
                        <p>You've blocked notifications. To enable them:</p>
                        <ol>
                          <li>Click the lock icon in your browser's address bar</li>
                          <li>Find "Notifications" and change it to "Allow"</li>
                          <li>Refresh this page</li>
                        </ol>
                      </div>
                    </div>
                  ) : notificationPermission === 'granted' ? (
                    <div className="notification-success">
                      <CheckCircle size={20} />
                      <div>
                        <strong>Push notifications are enabled</strong>
                        <p>You'll receive notifications for important updates and announcements.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="notification-action">
                      <button
                        onClick={handleEnablePushNotifications}
                        disabled={loading}
                        className="btn-enable-notifications"
                      >
                        <Bell size={18} />
                        {loading ? 'Enabling...' : 'Allow Browser Notifications'}
                      </button>
                      <p className="help-text">
                        Click to enable push notifications. Your browser will ask for permission.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* B) Email Notifications Section */}
              <div className="notification-section">
                <h3 className="section-title">
                  <FileText size={20} />
                  Email Notifications
                </h3>
                <p className="section-description">
                  Choose which emails you want to receive
                </p>

                <div className="notification-list">
                  <div className="notification-item">
                    <div className="notification-item-info">
                      <strong>Course updates & announcements</strong>
                      <p>Get notified when instructors post course updates</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.emailNotifications.courseUpdates}
                        onChange={() => handleEmailNotificationToggle('courseUpdates')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-item">
                    <div className="notification-item-info">
                      <strong>New lesson published</strong>
                      <p>Be the first to know when new lessons are available</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.emailNotifications.newLessons}
                        onChange={() => handleEmailNotificationToggle('newLessons')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-item">
                    <div className="notification-item-info">
                      <strong>Live webinar reminders</strong>
                      <p>Get reminded before webinars start</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.emailNotifications.webinarReminders}
                        onChange={() => handleEmailNotificationToggle('webinarReminders')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-item">
                    <div className="notification-item-info">
                      <strong>Assignment deadlines</strong>
                      <p>Reminders for upcoming assignment due dates</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.emailNotifications.assignmentDeadlines}
                        onChange={() => handleEmailNotificationToggle('assignmentDeadlines')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-item">
                    <div className="notification-item-info">
                      <strong>Certificate earned</strong>
                      <p>Celebrate your achievements with course completion certificates</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.emailNotifications.certificateEarned}
                        onChange={() => handleEmailNotificationToggle('certificateEarned')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-item">
                    <div className="notification-item-info">
                      <strong>Payment & billing notifications</strong>
                      <p>Important updates about subscriptions and payments</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.emailNotifications.paymentBilling}
                        onChange={() => handleEmailNotificationToggle('paymentBilling')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-item">
                    <div className="notification-item-info">
                      <strong>Marketing & promotional emails</strong>
                      <p>Special offers, new courses, and platform updates</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.emailNotifications.marketing}
                        onChange={() => handleEmailNotificationToggle('marketing')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              {/* C) Notification Frequency Section */}
              <div className="notification-section">
                <h3 className="section-title">
                  <Clock size={20} />
                  Email Frequency
                </h3>
                <p className="section-description">
                  How often do you want to receive email notifications?
                </p>

                <div className="frequency-options">
                  <label className={`frequency-option ${notificationPrefs.emailFrequency === 'instant' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="emailFrequency"
                      value="instant"
                      checked={notificationPrefs.emailFrequency === 'instant'}
                      onChange={() => handleEmailFrequencyChange('instant')}
                    />
                    <div className="frequency-option-content">
                      <Zap size={20} />
                      <div>
                        <strong>Instant</strong>
                        <p>Get emails as notifications happen (recommended)</p>
                      </div>
                    </div>
                  </label>

                  <label className={`frequency-option ${notificationPrefs.emailFrequency === 'daily' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="emailFrequency"
                      value="daily"
                      checked={notificationPrefs.emailFrequency === 'daily'}
                      onChange={() => handleEmailFrequencyChange('daily')}
                    />
                    <div className="frequency-option-content">
                      <Sun size={20} />
                      <div>
                        <strong>Daily Digest</strong>
                        <p>Receive one email per day with all updates</p>
                      </div>
                    </div>
                  </label>

                  <label className={`frequency-option ${notificationPrefs.emailFrequency === 'weekly' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="emailFrequency"
                      value="weekly"
                      checked={notificationPrefs.emailFrequency === 'weekly'}
                      onChange={() => handleEmailFrequencyChange('weekly')}
                    />
                    <div className="frequency-option-content">
                      <Calendar size={20} />
                      <div>
                        <strong>Weekly Summary</strong>
                        <p>Receive one email per week with a summary</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* D) Notification Categories Section */}
              <div className="notification-section">
                <h3 className="section-title">
                  <SettingsIcon size={20} />
                  Notification Categories
                </h3>
                <p className="section-description">
                  Control which types of notifications you want to receive
                </p>

                <div className="category-list">
                  <div className="category-item">
                    <div className="category-item-content">
                      <CheckCircle size={20} className="category-icon" />
                      <div>
                        <strong>Course content updates</strong>
                        <p>New lessons, course materials, and content changes</p>
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.categories.courseContent}
                        onChange={() => handleCategoryToggle('courseContent')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="category-item">
                    <div className="category-item-content">
                      <AlertCircle size={20} className="category-icon" />
                      <div>
                        <strong>System announcements</strong>
                        <p>Important platform updates and maintenance notices</p>
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.categories.systemAnnouncements}
                        onChange={() => handleCategoryToggle('systemAnnouncements')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="category-item">
                    <div className="category-item-content">
                      <Zap size={20} className="category-icon" />
                      <div>
                        <strong>Live streaming alerts</strong>
                        <p>Notifications when live webinars and sessions start</p>
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.categories.liveStreaming}
                        onChange={() => handleCategoryToggle('liveStreaming')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="category-item">
                    <div className="category-item-content">
                      <DollarSign size={20} className="category-icon" />
                      <div>
                        <strong>Promotional offers & discounts</strong>
                        <p>Special deals, course discounts, and promotional campaigns</p>
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.categories.promotions}
                        onChange={() => handleCategoryToggle('promotions')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="category-item">
                    <div className="category-item-content">
                      <CreditCard size={20} className="category-icon" />
                      <div>
                        <strong>Account & billing updates</strong>
                        <p>Payment confirmations, subscription changes, and invoices</p>
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.categories.accountBilling}
                        onChange={() => handleCategoryToggle('accountBilling')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
